// Small pieces the calls screens share: the bars of a call in progress, a
// row's mark, the second-by-second clock of the elapsed time, and how a call
// is said in one line.
import { useQuery } from "@tanstack/react-query";
import type { VoiceRoomView } from "@trameria/cloud-api/voice-shared";
import { m } from "@trameria/cloud-i18n";
import { useApi, useFormat } from "@trameria/ui-core";
import { useEffect, useState } from "react";
import { membersQuery } from "../workspace";

/** The bars beside a call in progress (still, not moving). */
export function EqBars({ small = false }: { small?: boolean }) {
  const heights = small ? [5, 9, 6] : [6, 11, 7];
  return (
    <span
      aria-hidden="true"
      className={`flex flex-none items-end ${small ? "gap-[1.5px]" : "gap-[2px]"}`}
      style={{ height: small ? 9 : 11 }}
    >
      {heights.map((h, i) => (
        <span
          // biome-ignore lint/suspicious/noArrayIndexKey: three fixed bars
          key={i}
          className={`${small ? "w-[2px]" : "w-[2.5px]"} bg-live`}
          style={{ height: h }}
        />
      ))}
    </span>
  );
}

/** A row's mark: a filled dot for a call in progress, a ring for an ended one. */
export function CallMark({ live }: { live: boolean }) {
  return live ? (
    <span
      role="img"
      aria-label={m.vc_live_mark()}
      className="box-border h-[11px] w-[11px] rounded-full border-[1.5px] border-transparent bg-live bg-clip-content"
    />
  ) : (
    <span
      role="img"
      aria-label={m.vc_ended_mark()}
      className="box-border h-2 w-2 rounded-full border-[1.5px] border-field"
    />
  );
}

/**
 * Now, moved on every second while `active` (the elapsed time). Not a fetch:
 * it only asks the screen to draw again.
 */
export function useSecondClock(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  // frontend-development: a clock for the elapsed time, no data is read.
  useEffect(() => {
    if (!active) return;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [active]);
  return now;
}

const pad2 = (n: number) => String(n).padStart(2, "0");

/** mm:ss, or h:mm:ss after an hour. */
export function elapsedLabel(startedAt: number, now: number): string {
  const total = Math.max(0, Math.floor((now - startedAt) / 1000));
  const h = Math.floor(total / 3600);
  const min = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return h > 0 ? `${h}:${pad2(min)}:${pad2(s)}` : `${pad2(min)}:${pad2(s)}`;
}

/** Each person once (a person can have several stays, one after another). */
export const peopleOf = (
  rows: readonly { userId: string; leftAt: string | null }[],
  onlyPresent: boolean,
) => [
  ...new Set(
    rows.filter((p) => !onlyPresent || p.leftAt === null).map((p) => p.userId),
  ),
];

/** The members' names in this workspace (a former member is said so). */
export function useMemberNames(slug: string): (userId: string) => string {
  const members = useQuery(membersQuery(useApi(), slug)).data ?? [];
  return (userId) =>
    members.find((p) => p.userId === userId)?.displayName ??
    m.vc_unknown_member();
}

/**
 * A call in one line: who is in it (or nobody yet) while in progress; when it
 * ended, how long and how many people took part.
 */
export function useCallLine(slug: string): (room: VoiceRoomView) => string {
  const nameOf = useMemberNames(slug);
  const format = useFormat();
  return (room) => {
    if (room.endedAt === null) {
      const present = peopleOf(room.participants, true);
      return present.map(nameOf).join(" · ") || m.vc_nobody();
    }
    const min = Math.max(
      1,
      Math.round(
        (Date.parse(room.endedAt) - Date.parse(room.createdAt)) / 60_000,
      ),
    );
    return m.vc_ended_sub({
      when: format.dateTime(room.endedAt),
      duration: m.vc_minutes({ min }),
      people: m.vc_people({
        count: peopleOf(room.participants, false).length,
      }),
    });
  };
}
